import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ordersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Loader2, ArrowLeft, Package } from 'lucide-react';

const SERVICE_TYPES = [
  { value: 'photo_restoration', label: 'Photo Restoration', description: 'Restore and enhance old or damaged photos' },
  { value: 'lora_training', label: 'LoRA Training', description: 'Train a custom AI model on your images' },
  { value: 'photo_scanning', label: 'Photo Scanning', description: 'Professional digitization of physical photos' },
  { value: 'digital_persona', label: 'Digital Persona', description: 'Create a digital AI persona/avatar' },
  { value: 'general_upload', label: 'General Upload', description: 'Upload files for other services' },
];

export default function NewOrder() {
  const navigate = useNavigate();
  const [serviceType, setServiceType] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!serviceType) {
      setError('Please select a service type');
      return;
    }

    setLoading(true);

    try {
      const order = await ordersAPI.create({
        service_type: serviceType,
        title: title || undefined,
        notes: notes || undefined,
      });
      navigate(`/orders/${order.id}`);
    } catch (err) {
      setError(err.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  const selectedService = SERVICE_TYPES.find(s => s.value === serviceType);

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Button 
            asChild
            variant="ghost" 
            className="mb-4 text-[#E8E4DE]/60 hover:text-white hover:bg-white/5 -ml-2"
          >
            <Link to="/orders">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Orders
            </Link>
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#C6A14A]/20 rounded-full flex items-center justify-center">
              <Package className="h-6 w-6 text-[#C6A14A]" />
            </div>
            <div>
              <h1 className="text-3xl font-light text-white tracking-wide">Create New Order</h1>
              <p className="text-[#E8E4DE]/60 mt-1">Start a new project and upload your files</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          <Card className="bg-[#0f1724] border-white/10">
            <CardHeader>
              <CardTitle className="text-xl text-white">Order Details</CardTitle>
              <CardDescription className="text-[#E8E4DE]/60">
                Select your service type and provide any additional details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Service Type */}
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/80">
                  Service Type <span className="text-red-400">*</span>
                </Label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger 
                    className="bg-[#050816]/50 border-white/10 text-white focus:border-[#C6A14A]"
                    data-testid="service-type-select"
                  >
                    <SelectValue placeholder="Select a service..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f1724] border-white/10">
                    {SERVICE_TYPES.map((service) => (
                      <SelectItem 
                        key={service.value} 
                        value={service.value}
                        className="text-white hover:bg-white/10 focus:bg-white/10"
                      >
                        {service.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedService && (
                  <p className="text-sm text-[#E8E4DE]/40">{selectedService.description}</p>
                )}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/80">
                  Order Title <span className="text-[#E8E4DE]/40">(optional)</span>
                </Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Grandma's Wedding Photos"
                  className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A]"
                  data-testid="order-title-input"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-[#E8E4DE]/80">
                  Notes <span className="text-[#E8E4DE]/40">(optional)</span>
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special instructions or details about your project..."
                  rows={4}
                  className="bg-[#050816]/50 border-white/10 text-white placeholder:text-white/30 focus:border-[#C6A14A] resize-none"
                  data-testid="order-notes-input"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/orders')}
                  className="border-white/10 text-[#E8E4DE] hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !serviceType}
                  className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] disabled:opacity-50"
                  data-testid="create-order-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Order'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}
